import { useCallback, useEffect, useMemo, useRef } from "react"
import { useSearchParams } from "react-router-dom"

import type { ProjectQuickViewCounts } from "../redux/projects.types.ts"

export type ProjectQuickView = keyof ProjectQuickViewCounts
export type ProjectScope = "mine" | "all"

export const PROJECT_QUICK_VIEWS: ProjectQuickView[] = [
  "all",
  "active",
  "attention",
  "closing_soon",
  "on_hold",
  "closed"
]

const QUICK_VIEW_PARAM = "quick_view"
const SCOPE_PARAM = "scope"

const asQuickView = (value: string | null): ProjectQuickView =>
  PROJECT_QUICK_VIEWS.includes(value as ProjectQuickView)
    ? (value as ProjectQuickView)
    : "all"

const asScope = (value: string | null): ProjectScope =>
  value === "mine" ? "mine" : "all"

/**
 * The quick view and the My/All scope live in the URL, not the persisted
 * filters: a link can name them, and Back steps through them. Each change is a
 * history entry for that reason. The defaults are left out of the URL so the
 * plain list keeps its plain address.
 */
export function useProjectsUrlState({ closedAllowed = true } = {}) {
  const [params, setParams] = useSearchParams()
  const named = asQuickView(params.get(QUICK_VIEW_PARAM))
  // A shared link can name Closed for someone without the Archive right; they
  // land on All rather than on a refusal.
  const quickView = named === "closed" && !closedAllowed ? "all" : named
  const scope = asScope(params.get(SCOPE_PARAM))

  const update = useCallback(
    (name: string, value: string, fallback: string) => {
      setParams((current) => {
        const next = new URLSearchParams(current)
        if (value === fallback) next.delete(name)
        else next.set(name, value)
        return next
      })
    },
    [setParams]
  )

  const setQuickView = useCallback(
    (view: ProjectQuickView) => update(QUICK_VIEW_PARAM, view, "all"),
    [update]
  )
  const setScope = useCallback(
    (next: ProjectScope) => update(SCOPE_PARAM, next, "all"),
    [update]
  )

  /** Back to All projects and the All view, in one history entry. */
  const resetViews = useCallback(
    () =>
      setParams((current) => {
        const next = new URLSearchParams(current)
        next.delete(QUICK_VIEW_PARAM)
        next.delete(SCOPE_PARAM)
        return next
      }),
    [setParams]
  )

  return useMemo(
    () => ({ quickView, scope, setQuickView, setScope, resetViews }),
    [quickView, scope, setQuickView, setScope, resetViews]
  )
}

/** The toolbar filters a link and Back carry. Search, sort and paging stay in
 *  the persisted slice, as before. */
export const URL_FILTER_KEYS = [
  "project_status",
  "project_manager",
  "project_type",
  "schedule_health",
  "customer_name",
  "end_date_after",
  "end_date_before",
  "created_window",
  "created_after",
  "created_before"
] as const

type UrlFilterKey = (typeof URL_FILTER_KEYS)[number]
type UrlFilters = Partial<Record<UrlFilterKey, string | null>>

const LIST_KEYS = new Set<UrlFilterKey>([
  "project_status",
  "project_manager",
  "project_type",
  "schedule_health"
])

/** One canonical string per key: a list is comma-joined, empty is absent. */
export const encodeUrlFilters = (
  filters: object | null | undefined
): UrlFilters => {
  const encoded: UrlFilters = {}
  for (const key of URL_FILTER_KEYS) {
    const value = (filters as Record<string, unknown> | null | undefined)?.[key]
    const text = Array.isArray(value)
      ? value.map(String).join(",")
      : value == null
        ? ""
        : String(value)
    if (text) encoded[key] = text
  }
  return encoded
}

/** The slice patch a URL describes: every key, so an absent one clears. */
export const decodeUrlFilters = (
  params: URLSearchParams
): Record<UrlFilterKey, string | string[] | null> => {
  const patch = {} as Record<UrlFilterKey, string | string[] | null>
  for (const key of URL_FILTER_KEYS) {
    const raw = params.get(key)
    patch[key] = !raw ? null : LIST_KEYS.has(key) ? raw.split(",") : raw
  }
  return patch
}

const sameFilters = (a: UrlFilters, b: UrlFilters) =>
  URL_FILTER_KEYS.every((key) => (a[key] ?? "") === (b[key] ?? ""))

const refinesCustomerText = (before: UrlFilters, after: UrlFilters) =>
  !!before.customer_name &&
  !!after.customer_name &&
  URL_FILTER_KEYS.every(
    (key) =>
      key === "customer_name" || (before[key] ?? "") === (after[key] ?? "")
  )

/**
 * Keeps the toolbar filters and the address bar in step, both ways.
 *
 * A filter change becomes a history entry, so Back restores the previous
 * filters; Back (or a pasted link) writes the URL's filters into the slice. On
 * the first render a link that names filters wins; a bare address takes the
 * filters the slice remembered, without adding a history entry.
 */
export function useFilterUrlSync(
  filters: object | null | undefined,
  applyFilters: (patch: Record<string, unknown>) => void,
  enabled: boolean
) {
  const [params, setParams] = useSearchParams()
  const mounted = useRef(false)

  const fromUrl = useMemo(
    () => encodeUrlFilters(decodeUrlFilters(params)),
    [params]
  )
  const fromState = useMemo(() => encodeUrlFilters(filters), [filters])

  const writeUrl = useCallback(
    (next: UrlFilters, replace: boolean) =>
      setParams(
        (current) => {
          const updated = new URLSearchParams(current)
          for (const key of URL_FILTER_KEYS) {
            const value = next[key]
            if (value) updated.set(key, value)
            else updated.delete(key)
          }
          return updated
        },
        { replace }
      ),
    [setParams]
  )

  // First commit only: a link that names filters wins; a bare address takes
  // what the slice remembered, replacing the entry rather than adding one.
  useEffect(() => {
    if (!enabled || mounted.current) return
    mounted.current = true
    if (Object.keys(fromUrl).length) {
      if (!sameFilters(fromUrl, fromState))
        applyFilters(decodeUrlFilters(params))
    } else if (Object.keys(fromState).length) {
      writeUrl(fromState, true)
    }
  }, [enabled])

  // The address moved (Back, Forward, a link): the slice follows it.
  const previousUrl = useRef(fromUrl)
  useEffect(() => {
    const moved = !sameFilters(previousUrl.current, fromUrl)
    previousUrl.current = fromUrl
    if (!enabled || !mounted.current || !moved) return
    if (!sameFilters(fromUrl, fromState)) applyFilters(decodeUrlFilters(params))
  }, [fromUrl, enabled])

  // The filters moved (picked, removed, cleared): one history entry each,
  // except while the Customer text is being typed, which refines one entry.
  const previousState = useRef(fromState)
  useEffect(() => {
    const before = previousState.current
    const moved = !sameFilters(before, fromState)
    previousState.current = fromState
    if (!enabled || !mounted.current || !moved) return
    if (!sameFilters(fromState, fromUrl))
      writeUrl(fromState, refinesCustomerText(before, fromState))
  }, [fromState, enabled])
}
